import {
  applicationStore,
  freeBetStore,
  gameConfigStore,
  gameResultStore,
  popupStore,
  roundInfoStore,
  userCountStore,
  userInfoStore,
  userWinCountStore,
  userSettingsStore,
  loadingStore,
  BetStatus,
  RoundStatus,
  tableBetHistoryStore,
} from '@pascal/pascal-core';

import { PopUps } from '@pascal/pascal-core/table-line/t_types/enums/PopupTypes';
import { roundTimeStore } from '../store/roundTimeStore';

import { worker } from '../index';
import { liveConnection } from '../core';
import { gameLiveConnection } from './GameConnection';
import { gameStore } from '../store/gameStore';
import { doBetStore } from '../store/doBetStore';
import { limitsAndPayoutsStore } from '../store/limitsAndPayoutsStore';
import { soundService, soundTypes } from './SoundService';
import { cardsStore } from '../store/cardsStore';
import { winningAmountStore } from '../store/winningAmountStore';
import { BOARD_CARD_GROUP_TYPES, ROUND_PHASE_TYPES } from '../enums/betTypesEnum';

let timeOutId;

class GameService {
  onGetInitialStateResult({
    rate,
    cards,
    limits,
    balance,
    precision,
    roundInfo,
    currencyCode,
    gameConfiguration,
    gameTypeInfo,
    userFreebets,
  }) {
    tableBetHistoryStore.setFetchPaths({ ThirdStep: '/betDetails' });
    tableBetHistoryStore.setFetchParamNames({ Id: 'RoundId' });
    freeBetStore.setIsFreeChip(true);

    const { limitsAndPayouts } = gameConfiguration;
    this.handleResetDataInitial();
    userInfoStore.setUserInfo({
      rate,
      balance,
      precision,
      currencyCode,
    });
    freeBetStore.setIsFreeChip(true);
    const nextCards = cards
      ?.filter(({ roundId }) => !roundId || (roundInfo && roundId === roundInfo?.roundId))
      .reverse();
    gameResultStore.setGameResults(nextCards);
    doBetStore.setQuickBets(limits.quickBets);
    if (!freeBetStore.activeFreeBet.id) {
      doBetStore.setSelectedChip(limits.quickBets[0]);
    }
    gameConfigStore.setLimits(limits);
    roundInfoStore.setRoundInfo({
      ...gameTypeInfo,
      duration: gameConfiguration.duration,
      roundId: roundInfo ? roundInfo.roundId : null,
    });
    if (
      roundInfo &&
      roundInfo.roundPhaseInfo?.second &&
      roundInfo.roundPhaseInfo.second > 0 &&
      gameConfiguration.duration - roundInfo.roundPhaseInfo.second > 0
    ) {
      const time = Math.floor(
        Math.abs(gameConfiguration.duration - roundInfo.roundPhaseInfo.second)
      );
      roundTimeStore.setTime(time);
      this.sendWorker(time);
      gameStore.setIsBetsClosed(false);
      if (!roundInfo.roundPhaseInfo.roundPhase) {
        gameStore.setIsRoundStarted(false);
      }
    }

    if (roundInfo) {
      gameStore.setRoundPhase(roundInfo.roundData?.currentPhase);
      if (roundInfo.roundData?.cards.length) {
        roundInfo.roundData.cards.forEach((card) => {
          cardsStore.updateRoundCards(card, roundInfo.roundId);
        });
      }
      if (roundInfo.roundData?.possibleBetTypes) {
        gameStore.setRoundBetTypesInfo(roundInfo.roundData.possibleBetTypes);
      }
      if (roundInfo.userBets.length && roundInfo.status !== RoundStatus.Finished) {
        const currentUserBets = roundInfo?.userBets.reduce(
          (acc, { betData, winAmount, ...all }) => {
            if (betData?.additionalBets?.length) {
              acc.push(
                ...betData.additionalBets.map((item) => ({
                  ...item,
                  betData: { ...item, roundPhase: betData.roundPhase },
                  marketType: BOARD_CARD_GROUP_TYPES.includes(item.groupType)
                    ? `${item.groupType}-${item.marketType}`
                    : item.marketType,
                }))
              );
            } else {
              acc.push({
                ...all,
                betData,
                marketType: betData?.marketType,
              });
            }
            return acc;
          },
          []
        );
        const userBetInRoundPhase = currentUserBets.filter(
          (item) => item.betData?.roundPhase === roundInfo.roundData?.currentPhase
        );

        if (currentUserBets.length) {
          const roundBetsByPhase = currentUserBets.reduce((acc, cur) => {
            if (acc[cur.betData.roundPhase]) {
              acc[cur.betData.roundPhase].push(cur);
            } else {
              acc[cur.betData.roundPhase] = [cur];
            }
            return acc;
          }, {});
          gameStore.setRoundBetsByPhase(roundBetsByPhase);
        }

        if (roundInfo.userBets[0].freeBetId) {
          freeBetStore.setActivateFreeBet(
            userFreebets.find((item) => item.id === roundInfo.userBets[0].freeBetId)
          );
        }
        doBetStore.setBetsModel({
          betModel: userBetInRoundPhase,
        });
      }
    }
    limitsAndPayoutsStore.setLimitsAndPayouts(limitsAndPayouts);

    loadingStore.updateLoading({ isGameInitialLoaded: true });
    popupStore.toggleReconnect(false);
    popupStore.onConnectionError(false);
    popupStore.close();
  }

  handleDoBetResult({ responseCode, bets }) {
    const { userId, demoUserId } = userInfoStore;
    userCountStore.setUsersCount(
      bets.length ? userCountStore.usersCount + 1 : userCountStore.usersCount
    );
    const currentUserBets = bets
      ?.filter(
        (bet) =>
          (+bet.userId === +userId || +bet.userId === +demoUserId) &&
          bet.status !== BetStatus.Canceled
      )
      .reduce((acc, { betData, winAmount, ...all }) => {
        if (betData?.markets?.additionalBets?.length) {
          acc.push(
            ...betData.markets.additionalBets.map(({ winAmount: win, ...item }) => ({
              ...item,
              betData,
              marketType: BOARD_CARD_GROUP_TYPES.includes(item.groupType)
                ? `${item.groupType}-${item.marketType}`
                : item.marketType,
            }))
          );
        } else {
          acc.push({
            ...all,
            betData,
            odd: betData.odd,
            marketType: betData?.markets?.marketType,
          });
        }
        return acc;
      }, []);
    if (currentUserBets.length) {
      doBetStore.setBetsModel({
        betModel: currentUserBets,
      });
      if (userSettingsStore.isSoundOn) {
        soundService.playBetAccepted();
      }
      doBetStore.handleNotificationCode(responseCode);
      gameStore.updateRoundBetsByPhase(currentUserBets);
    }
  }

  handleDoBetResultValidation({ responseCode }) {
    doBetStore.resetBetsModels();
    doBetStore.handleNotificationCode(responseCode);
  }

  handleRoundStart({ data: { startedRound } }) {
    userCountStore.setUsersCount(0);
    roundInfoStore.setRoundId(startedRound.roundId);
    this.sendWorker(roundInfoStore.duration);
    this.handleResetEndedRoundData();
    gameStore.setIsBetsClosed(false);
  }

  handleResultReport({ data: { resultReportData, roundId } }) {
    const { openedCard, roundPhase, betTypes, hasWiningCombination, newCards } = resultReportData;
    if (openedCard) {
      gameStore.setGameStatus(null);
      soundService.play(soundTypes.Card);
      if (roundInfoStore.roundId === roundId) {
        cardsStore.updateRoundCards(openedCard, roundId);
      }
    }
    if (Number.isInteger(roundPhase)) {
      doBetStore.resetBetsModels();
      gameStore.setRoundPhase(roundPhase);
      gameStore.setIsBetsClosed(false);
      userCountStore.setUsersCount(0);
      if (!hasWiningCombination) {
        this.sendWorker(roundInfoStore.duration);
      }
    }
    if (betTypes) {
      gameStore.setRoundBetTypesInfo(betTypes);
    }
    if (newCards) {
      gameResultStore.setGameResults(newCards.reverse());
    }
  }

  // async handleGetBalance() {
  //   const {
  //     data: { result },
  //   } = await ApiServices.get(ApiServiceTypes.UsersService).getBalance();
  //   if (result) {
  //     userInfoStore.setBalance(result.balance);
  //   }
  // }

  // handleGetBalanceResult(balance) {
  //   userInfoStore.setBalance(balance);
  // }

  handleRoundFin({ data: { finishedRound, bets } }) {
    const { roundData } = finishedRound;
    const { userId, demoUserId } = userInfoStore;
    const { freeBetTotalWinCalculator } = freeBetStore;

    gameStore.setRoundBetTypesInfo(
      roundData.possibleBetTypes.find((item) => item.roundPhase === ROUND_PHASE_TYPES.PreRiver)
        .betTypes
    );
    winningAmountStore.setWinningCombination(roundData.winningCombination);
    winningAmountStore.setWinningBoardCardTypes(roundData.boardCardWinTypes);
    winningAmountStore.setWinningBoxes(roundData.winningBoxes);
    cardsStore.setDealerCards(roundData.cards.filter((card) => card.boxIndex === null));

    const currentUserBets = bets?.filter(
      (bet) => +bet.userId === +userId || +bet.userId === +demoUserId
    );
    if (currentUserBets?.length) {
      const totalWinAmount = currentUserBets.reduce((acc, cur) => {
        acc += cur.winAmount;
        return acc;
      }, 0);
      winningAmountStore.setWinningAmount(totalWinAmount);
      winningAmountStore.setLastWin(totalWinAmount);
      setTimeout(() => {
        if (currentUserBets?.[0].freeBetId) {
          freeBetTotalWinCalculator({ winAmount: totalWinAmount });
        }
        // gameLiveConnection.getBalance();
      }, 500);
      if (!applicationStore.isDemo) {
        setTimeout(() => {
          tableBetHistoryStore.handleBetHistoryUpdate();
        }, 2000);
      }
    }
    if (bets) {
      const winingUsers = bets.filter((user) => user.winAmount !== 0 && user.userId > 0);
      if (winingUsers.length) {
        winingUsers.sort((a, b) => b.winAmount - a.winAmount);
        userWinCountStore.setUserWiningBets(winingUsers);
      }
      setTimeout(() => {
        userWinCountStore.setUserWiningBets(null);
      }, 5000);
    }
  }

  handleBetsClosed({ data: { gameTypeId, roundId } }) {
    const { lastBetModel } = doBetStore;
    const { activeFreeBet } = freeBetStore;

    const betData = lastBetModel?.betModel?.map((bet) => {
      if (!Number.isInteger(bet.marketType)) {
        return {
          ...bet,
          marketType: bet.marketType?.split('-')?.[1],
        };
      }
      return bet;
    });
    gameStore.setIsBetsClosed(true);
    gameStore.handleChangeStatus('LETS_DEAL');
    gameStore.setIsRoundStarted(true);
    if (betData?.length) {
      gameLiveConnection.doBet(
        activeFreeBet.id
          ? {
              betData,
              gameTypeId,
              roundId,
              freeBetId: activeFreeBet.id,
            }
          : {
              betData,
              gameTypeId,
              roundId,
            }
      );
    }
  }

  socketRecursiveReconnecting() {
    try {
      liveConnection
        .restartGameConnection()
        .then((connectionData) => {
          liveConnection.connectionInitCallback(connectionData);
          clearTimeout(timeOutId);
        })
        .catch(() => {
          timeOutId = setTimeout(() => {
            this.socketRecursiveReconnecting();
          }, 4000);
        });
    } catch (err) {
      // popupStore.open({ name: PopUps.ConnectionError });
      popupStore.onConnectionError(true);
      popupStore.toggleReconnect(true);
    }
  }

  handleReconnecting(error) {
    if (error && error.message) {
      // eslint-disable-next-line
      popupStore.open({ name: PopUps.Reconnect });
      popupStore.toggleReconnect(true);
    } else {
      GameService.ReconnectCount += 1;
      popupStore.open({ name: PopUps.Reconnect });
      popupStore.toggleReconnect(true);
    }
  }

  handleReconnected() {
    GameService.ReconnectCount = 0;
    if (popupStore.popupName !== PopUps.ConnectionError) {
      gameLiveConnection.sendGameGetInitialState(false).then(() => {});
    }
  }

  handleConnectionError() {
    if (GameService.ReconnectCount) {
      // popupStore.open({ name: PopUps.ConnectionError });
      popupStore.onConnectionError(true);
    }
  }

  countDown({ data: time }) {
    roundTimeStore.setTime(time);
  }

  sendWorker(timerValue) {
    if (timerValue) {
      worker.addEventListener('message', this.countDown);
      worker.postMessage(timerValue);
    } else {
      worker.removeEventListener('message', this.countDown);
    }
  }

  handleResetDataInitial() {
    worker.removeEventListener('message', this.countDown);
    this.handleResetEndedRoundData();
    gameStore.setIsBetsClosed(true);
    gameStore.setIsRoundStarted(true);
  }

  handleResetEndedRoundData() {
    gameStore.setIsRoundStarted(false);
    gameStore.setGameStatus(null);
    cardsStore.resetCardsInfo();
    gameStore.resetRoundBetsByPhase();
    winningAmountStore.setWinningBoxes(null);
    gameStore.setRoundBetTypesInfo(null);
    doBetStore.resetBetsModels(true);
    winningAmountStore.setWinningAmount(null);
    winningAmountStore.setWinningCombination(null);
    winningAmountStore.setWinningBoardCardTypes(null);
    winningAmountStore.setWinningBoxes(null);
  }
}

export const gameService = new GameService();
